import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Image as ImageIcon, Search, Trash2, Link as LinkIcon, Loader2, Wand2, Sparkles, Coins, X as XIcon } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };

/**
 * ImagePicker — modal dialog for choosing an image to insert into the canvas.
 *
 * Three tabs:
 *  - Upload  : drag/drop or browse from local disk; uploaded files are
 *              auto-saved to the user's Media Library and returned as a URL.
 *  - Library : grid of user's existing image assets.
 *  - URL     : paste any image URL.
 *
 * Props:
 *   open   : boolean
 *   onClose: () => void
 *   onPick : (url: string) => void   // called with absolute URL when chosen
 */
export default function ImagePicker({ open, onClose, onPick }) {
  const { refresh } = useAuth();
  const [tab, setTab] = useState("upload");
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingBgId, setRemovingBgId] = useState(null);  // asset id currently being processed
  const [urlValue, setUrlValue] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  // AI provider picker — fetched lazily on first open of the AI tab so the
  // user can choose Nano Banana (free, fast) or GPT Image 1 (premium, sharper).
  const [aiProviders, setAiProviders] = useState([]);
  const [aiProvider, setAiProvider] = useState(() => localStorage.getItem("nexso_ai_provider") || "gemini");
  // Credits — pulled from /ai/providers alongside provider list.
  const [creditsBalance, setCreditsBalance] = useState(0);
  const [creditsPerImage, setCreditsPerImage] = useState(5);
  // Reference image for image-to-image (Premium — 5 credits regardless of provider).
  const [referenceFile, setReferenceFile] = useState(null);  // { file_id, url, name }
  const [uploadingRef, setUploadingRef] = useState(false);
  const fileRef = useRef(null);
  const referenceInputRef = useRef(null);
  const dropRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const r = await api.get(`/assets?${params.toString()}`);
      const imgs = (r.data.assets || []).filter((a) => a.kind === "image");
      setAssets(imgs);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    if (open && tab === "library") loadLibrary();
  }, [open, tab, loadLibrary]);

  // Fetch provider list once when the AI tab is first activated. Cheap GET.
  useEffect(() => {
    if (open && tab === "ai" && aiProviders.length === 0) {
      api.get("/ai/providers").then((r) => {
        setAiProviders(r.data?.providers || []);
      }).catch(() => { /* non-blocking — UI falls back to single Generate btn */ });
    }
  }, [open, tab, aiProviders.length]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTab("upload");
      setUrlValue("");
      setQ("");
      setAiPrompt("");
      setReferenceFile(null);
    }
  }, [open]);

  const uploadFile = async (file) => {
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/") || /\.(mp4|webm|mov|m4v)$/i.test(file.name || "");
    if (!isImage && !isVideo) {
      toast.error("Please select an image or video file");
      return;
    }
    // Cheap Android WebViews choke on >50 MB video — bail before uploading.
    if (isVideo && file.size > 50 * 1024 * 1024) {
      toast.error(`Video too large (${Math.round(file.size / (1024 * 1024))} MB). Max is 50 MB — please compress with HandBrake and try again.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Save into Media Library so it's reusable later
      const asset = await api.post("/assets", {
        name: file.name,
        file_id: up.data.id,
        kind: isVideo ? "video" : "image",
        folder: "",
      });
      const url = asset.data.url || up.data.url;
      toast.success(isVideo ? "Video saved to Media Library" : "Saved to Media Library");
      onPick(url, isVideo ? "video" : "image");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) uploadFile(f);
  };

  const pickUrl = () => {
    const v = (urlValue || "").trim();
    if (!v) {
      toast.error("Please paste an image URL");
      return;
    }
    onPick(v);
    onClose();
  };

  const removeAsset = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this image from your Media Library?")) return;
    try {
      await api.delete(`/assets/${id}`);
      setAssets((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeBackground = async (asset, e) => {
    e.stopPropagation();
    if (!asset.file_id) {
      toast.error("Background removal works on uploaded images only");
      return;
    }
    setRemovingBgId(asset.id);
    const toastId = toast.loading("Removing background…");
    try {
      const r = await api.post("/images/remove-bg", {
        file_id: asset.file_id,
        name: asset.name?.replace(/\.[^.]+$/, "") || "image",
      });
      toast.success("Background removed — saved to Media Library", { id: toastId });
      // Refresh library so the new transparent asset shows up at the top
      await loadLibrary();
      // Auto-pick the new image so the user gets instant gratification
      onPick(r.data.url);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err), { id: toastId });
    } finally {
      setRemovingBgId(null);
    }
  };

  const uploadReference = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file for the reference");
      return;
    }
    setUploadingRef(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReferenceFile({ file_id: up.data.id, url: up.data.url, name: file.name });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingRef(false);
      if (referenceInputRef.current) referenceInputRef.current.value = "";
    }
  };

  const generateImage = async () => {
    const p = (aiPrompt || "").trim();
    if (!p) {
      toast.error("Please describe the image you want");
      return;
    }
    // Reference-image blends always route through Nano Banana (free).
    // Premium = only OpenAI text-to-image (5 credits).
    const hasReference = !!referenceFile?.file_id;
    const isPremium = !hasReference && aiProvider === "openai";
    // Client-side guard so users don't hit Stripe checkout mid-generation.
    if (isPremium && creditsBalance < creditsPerImage) {
      toast.error(`You need ${creditsPerImage} credits for this — you have ${creditsBalance}. Buy a credit pack in Credits.`);
      return;
    }
    setAiBusy(true);
    const isOpenAI = aiProvider === "openai";
    const tId = toast.loading(
      hasReference
        ? "Blending your reference with Nano Banana… (~15s)"
        : isOpenAI
          ? "Generating with GPT Image 1… (~30-60s, premium model)"
          : "Generating with Nano Banana… (~10s)"
    );
    try {
      const payload = { prompt: p, provider: aiProvider };
      if (hasReference) payload.reference_file_id = referenceFile.file_id;
      const r = await api.post("/ai/image", payload);
      const label = hasReference
        ? "AI blend"
        : isOpenAI ? "GPT Image 1" : "Nano Banana";
      const chargedMsg = r.data.credits_charged > 0
        ? ` · -${r.data.credits_charged} credits (${r.data.credits_remaining} left)`
        : "";
      toast.success(`Image generated by ${label}${chargedMsg} · saved to Media Library`, { id: tId });
      // Remember the user's choice so it sticks across sessions.
      localStorage.setItem("nexso_ai_provider", aiProvider);
      // Refresh in-app credit balance so any subsequent generation reflects it.
      if (r.data.credits_remaining !== undefined) setCreditsBalance(r.data.credits_remaining);
      if (refresh) refresh();
      onPick(r.data.url);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err), { id: tId });
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl bg-white border-slate-200 text-slate-900"
        data-testid="image-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle style={headingFont} className="text-2xl">Add an image</DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            Upload, generate with AI, pick from your library, or paste a URL — your choice ships straight to the canvas.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="upload" data-testid="image-picker-tab-upload">
              <Upload className="h-4 w-4 mr-1.5" /> Upload
            </TabsTrigger>
            <TabsTrigger value="ai" data-testid="image-picker-tab-ai">
              <Sparkles className="h-4 w-4 mr-1.5" /> AI generate
            </TabsTrigger>
            <TabsTrigger value="library" data-testid="image-picker-tab-library">
              <ImageIcon className="h-4 w-4 mr-1.5" /> Media Library
            </TabsTrigger>
            <TabsTrigger value="url" data-testid="image-picker-tab-url">
              <LinkIcon className="h-4 w-4 mr-1.5" /> URL
            </TabsTrigger>
          </TabsList>

          {/* ─── Upload ─────────────────────────────────────────────── */}
          <TabsContent value="upload" className="mt-4">
            <div
              ref={dropRef}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`rounded-3xl border-2 border-dashed p-12 text-center transition-colors ${
                isDragging ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-slate-50"
              }`}
              data-testid="image-picker-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                hidden
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                data-testid="image-picker-file-input"
              />
              <Upload className="h-10 w-10 mx-auto text-sky-500 mb-3" />
              <div className="text-lg font-semibold mb-1" style={headingFont}>
                {uploading ? "Uploading…" : "Drop an image or video here"}
              </div>
              <div className="text-sm text-slate-500 mb-5">PNG, JPG, WEBP, GIF (≤8 MB) · MP4, WebM (≤50 MB)</div>
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="rounded-full bg-sky-500 hover:bg-sky-600 text-white"
                data-testid="image-picker-browse-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                {uploading ? "Uploading…" : "Browse from computer"}
              </Button>
              <div className="text-xs text-slate-400 mt-4">
                Your upload is automatically saved to your <strong>Media Library</strong> for reuse.
              </div>
            </div>
          </TabsContent>

          {/* ─── AI Generate ────────────────────────────────────────── */}
          <TabsContent value="ai" className="mt-4">
            <div className="rounded-3xl border-2 border-dashed border-violet-200 bg-gradient-to-br from-violet-50 to-sky-50 p-6 md:p-8">
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-violet-600" />
                  <div className="text-lg font-semibold" style={headingFont}>Describe an image</div>
                </div>
                {/* Credits pill — shows current balance + per-image cost */}
                <Link
                  to="/dashboard/credits"
                  onClick={onClose}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 bg-white border border-violet-200 rounded-full px-3 py-1.5 hover:bg-violet-100 transition"
                  data-testid="image-picker-credits-pill"
                >
                  <Coins className="h-3.5 w-3.5 text-amber-500" />
                  <span data-testid="image-picker-credits-balance">{creditsBalance}</span>
                  <span className="text-slate-400 font-normal">credits</span>
                </Link>
              </div>

              {/* Reference image dropzone — image-to-image blends via Nano
                  Banana. Free regardless of the picker choice. */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                    Reference image <span className="text-slate-400 normal-case tracking-normal">(optional — blend with a photo)</span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                    Free · Nano Banana
                  </span>
                </div>
                <input
                  ref={referenceInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => e.target.files?.[0] && uploadReference(e.target.files[0])}
                  data-testid="image-picker-reference-file-input"
                />
                {referenceFile ? (
                  <div className="flex items-center gap-3 rounded-xl border border-violet-300 bg-white p-2" data-testid="image-picker-reference-preview">
                    <img
                      src={referenceFile.url}
                      alt="Reference"
                      className="h-14 w-14 rounded-lg object-cover shrink-0 border"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate">{referenceFile.name}</div>
                      <div className="text-[10px] text-slate-500">Blends the prompt into this image</div>
                    </div>
                    <button
                      onClick={() => setReferenceFile(null)}
                      className="h-7 w-7 rounded-full bg-slate-100 hover:bg-rose-100 text-slate-500 hover:text-rose-600 flex items-center justify-center shrink-0"
                      title="Remove reference"
                      data-testid="image-picker-reference-clear"
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => referenceInputRef.current?.click()}
                    disabled={uploadingRef || aiBusy}
                    className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-violet-300 bg-white/60 hover:bg-white text-violet-700 text-xs font-semibold px-3 py-3 transition disabled:opacity-50"
                    data-testid="image-picker-reference-upload-btn"
                  >
                    {uploadingRef ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploadingRef ? "Uploading…" : "Upload a reference image to blend"}
                  </button>
                )}
              </div>

              {/* Provider picker — Nano Banana (free) vs GPT Image 1 (premium).
                  Rendered as 2 picker cards so customers see what they pick. */}
              {aiProviders.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mb-4" data-testid="image-picker-provider-grid">
                  {aiProviders.map((p) => {
                    const isSelected = aiProvider === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setAiProvider(p.id); localStorage.setItem("nexso_ai_provider", p.id); }}
                        disabled={aiBusy}
                        className={`text-left p-3 rounded-2xl border-2 transition-all ${
                          isSelected
                            ? "border-violet-500 bg-white shadow-sm"
                            : "border-slate-200 bg-white/60 hover:border-slate-300"
                        } disabled:opacity-50`}
                        data-testid={`image-picker-provider-${p.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-bold text-slate-900">{p.name}</span>
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 h-4 rounded-full flex items-center ${
                            p.badge === "Free"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {p.badge}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500">{p.subtitle}</div>
                        <div className="text-[10px] mt-1 flex items-center gap-1">
                          {(p.cost_credits ?? 0) > 0 ? (
                            <span className="inline-flex items-center gap-0.5 text-amber-700 font-semibold" data-testid={`image-picker-provider-${p.id}-cost`}>
                              <Coins className="h-2.5 w-2.5" /> {p.cost_credits} credits/img
                            </span>
                          ) : (
                            <span className="text-emerald-600 font-semibold">Free</span>
                          )}
                          <span className="text-slate-400 italic ml-1">· {p.best_for.slice(0, 2).join(", ")}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) generateImage();
                }}
                placeholder={referenceFile
                  ? "e.g. add golden hour lighting and a soft studio backdrop"
                  : "e.g. Steaming espresso cup on a rustic wooden table, soft morning light, advertising photo"}
                rows={4}
                className="w-full rounded-xl border border-violet-200 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"
                data-testid="image-picker-ai-prompt"
                disabled={aiBusy}
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[
                  "professional product photo, soft studio lighting",
                  "vibrant food close-up, restaurant menu style",
                  "minimal flat illustration, brand colors",
                  "modern storefront exterior at golden hour",
                ].map((suffix) => (
                  <button
                    key={suffix}
                    onClick={() => setAiPrompt((prev) => (prev ? `${prev}, ${suffix}` : suffix))}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-slate-600"
                    disabled={aiBusy}
                  >
                    + {suffix.split(",")[0]}
                  </button>
                ))}
              </div>

              {(() => {
                const hasReference = !!referenceFile;
                // Premium (credits) = only OpenAI text-to-image. Any reference
                // blend routes through Nano Banana (free), so keep it free.
                const isPremium = !hasReference && aiProvider === "openai";
                const cost = isPremium ? creditsPerImage : 0;
                const insufficient = isPremium && creditsBalance < cost;
                const modelLabel = hasReference
                  ? "Nano Banana blend"
                  : aiProvider === "openai" ? "GPT Image 1" : "Nano Banana";

                if (insufficient) {
                  return (
                    <div className="mt-4 space-y-2" data-testid="image-picker-insufficient-credits">
                      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-900">
                        <Coins className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                        <div>
                          <strong>You need {cost} credits</strong> for GPT Image 1 — you have {creditsBalance}. Nano Banana stays free (switch provider above), or top up to unlock GPT Premium.
                        </div>
                      </div>
                      <Link
                        to="/dashboard/credits"
                        onClick={onClose}
                        className="w-full h-11 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold flex items-center justify-center gap-2 transition"
                        data-testid="image-picker-buy-credits-btn"
                      >
                        <Coins className="h-4 w-4" /> Buy credits
                      </Link>
                    </div>
                  );
                }
                return (
                  <Button
                    onClick={generateImage}
                    disabled={aiBusy || !aiPrompt.trim()}
                    className="mt-4 w-full h-11 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold"
                    data-testid="image-picker-ai-generate"
                  >
                    {aiBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    {aiBusy
                      ? "Generating…"
                      : cost > 0
                        ? `Generate with ${modelLabel} · -${cost} credits`
                        : `Generate with ${modelLabel} · Free`}
                  </Button>
                );
              })()}

              <div className="mt-3 text-xs text-slate-500 text-center">
                Generated image is automatically saved to your <strong>Media Library</strong>.{" "}
                {referenceFile ? (
                  <>Reference blends use <strong className="text-emerald-700">Nano Banana</strong> — free, ~15s.</>
                ) : aiProvider === "openai" ? (
                  <>
                    GPT Image 1 takes ~30-60s · <span className="font-semibold text-violet-700">AI Director enhances your prompt automatically</span> for ChatGPT-app-quality results.
                  </>
                ) : (
                  "Nano Banana takes ~10s."
                )}
              </div>
            </div>
          </TabsContent>

          {/* ─── Library ────────────────────────────────────────────── */}
          <TabsContent value="library" className="mt-4">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search images…"
                className="pl-9 bg-slate-50 border-slate-200 rounded-full"
                data-testid="image-picker-search"
              />
            </div>
            {loading ? (
              <div className="py-16 text-center text-slate-400">
                <Loader2 className="h-6 w-6 mx-auto animate-spin" />
              </div>
            ) : assets.length === 0 ? (
              <div className="py-12 text-center" data-testid="image-picker-library-empty">
                <ImageIcon className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <div className="text-sm text-slate-500">
                  Your library is empty. Upload an image to get started.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto pr-1" data-testid="image-picker-library-grid">
                {assets.map((a) => {
                  const isProcessing = removingBgId === a.id;
                  return (
                  <button
                    key={a.id}
                    onClick={() => { if (!isProcessing) { onPick(a.url); onClose(); } }}
                    className="group relative rounded-xl overflow-hidden border border-slate-200 hover:border-sky-400 hover:shadow-md transition aspect-square"
                    style={{
                      // checkered background so transparency shows through
                      backgroundColor: "#fafafa",
                      backgroundImage:
                        "linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)",
                      backgroundSize: "16px 16px",
                      backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
                    }}
                    data-testid={`image-picker-asset-${a.id}`}
                  >
                    <img
                      src={a.url}
                      alt={a.name}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                    {isProcessing && (
                      <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center gap-2">
                        <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
                        <div className="text-[11px] font-semibold text-slate-600">Removing BG…</div>
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-left">
                      <div className="text-[11px] font-medium text-white truncate">{a.name}</div>
                    </div>
                    <div className="absolute top-1.5 right-1.5 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition">
                      {a.file_id && (
                        <button
                          onClick={(e) => removeBackground(a, e)}
                          className="h-7 px-2 rounded-full bg-white/95 hover:bg-sky-50 text-sky-700 text-[10px] font-bold uppercase tracking-wide shadow flex items-center gap-1"
                          title="Remove background with AI"
                          data-testid={`image-picker-removebg-${a.id}`}
                          disabled={isProcessing}
                        >
                          <Wand2 className="h-3 w-3" /> No BG
                        </button>
                      )}
                      <button
                        onClick={(e) => removeAsset(a.id, e)}
                        className="h-7 w-7 rounded-full bg-white/95 hover:bg-rose-50 text-rose-500 shadow flex items-center justify-center self-end"
                        title="Delete from library"
                        data-testid={`image-picker-delete-${a.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ─── URL ────────────────────────────────────────────────── */}
          <TabsContent value="url" className="mt-4">
            <div className="rounded-2xl border border-slate-200 p-6 bg-slate-50">
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 block">Image URL</label>
              <Input
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && pickUrl()}
                placeholder="https://example.com/image.jpg"
                className="bg-white border-slate-200"
                data-testid="image-picker-url-input"
              />
              <Button
                onClick={pickUrl}
                className="mt-4 rounded-full bg-sky-500 hover:bg-sky-600 text-white"
                data-testid="image-picker-url-add"
              >
                <LinkIcon className="h-4 w-4 mr-1.5" /> Add from URL
              </Button>
              <div className="text-xs text-slate-400 mt-3">
                Images from URLs are linked, not saved to your Media Library.
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
